/* eslint-disable import/no-cycle */
import axios from 'axios';
import { ADD_TO_CART_EVENTAPI } from './app/containers/Init/constants';
import { checkFreeGiftConditionsAndModifyCart, fetchCart } from './common/cart';
import { freeGiftlocalStorageTTL } from './common/constants';
import { StaticMutexKeys, mutexManager } from './common/mutexmanager';

import {
  getAutoAddFreeGiftsConfig,
  getManualFreeGiftsConfig,
  getWidgetOfferMapping,
  setAutoFreeGiftsConfig,
  setManualFreeGiftsConfig,
  setWidgetOfferMapping,
  getPdpFgVariantsWithQuantity,
  getPdpFgWidgetList,
} from './common/settings';

import { logger } from './common/logger';
import { getPage } from './common/pageData';
import { logRuntimeError, logRuntimeInfo } from './common/utils';

export async function onManualFreeGiftEligibleResponse({
  offer,
  mrCart,
  widget,
  isCalledFromCartDrawer,
  skipExecuteFgConditions = false,
}) {
  const {
    freeGiftAvailingType,
    offered,
    primary: { sp_variant_id: primaryVariantId },
    isCartRangeTriggerEnabled,
    cartRange,
    theme,
    type: offerType,
    isCartQuantityRestricted,
    currencyCode,
    defaultCurrencyCartRange,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isNumberOfOfferedProductsRestricted,
    isDiscountQuantityRestricted,
    offerLevelDiscountQuantity,
    discountQuantity,
    isEligibleOnlyForSubscription,
    mr_offer_id,
    reservedItemQuantity,
    rulesEligibilityCount,
    triggerType,
  } = offer;

  const { id, index } = widget;
  const widgetID = id.toString();

  if (offerType !== 'freegift') {
    return;
  }

  if (freeGiftAvailingType !== 'manual_selection') {
    return;
  }

  // in case a new offer is being displayed for the same widget
  // then run the conditions from the previous offer as false
  const widgetOfferMapping = getWidgetOfferMapping();
  const existingOfferID = widgetOfferMapping[widgetID.toString()];
  widgetOfferMapping[widgetID] = mr_offer_id;
  setWidgetOfferMapping(JSON.stringify(widgetOfferMapping));

  let isEligible = false;
  const isCartPage = getPage() === 'cart';
  const isPdpFg = triggerType === 'product_page';

  const autoRemoveConditionForPdpFg = isPdpFg
    ? isCartPage || isCalledFromCartDrawer
    : true;

  // in the future, if there are more conditions for auto-remove, we can modify the below variable.
  const canBeAutoRemoved = autoRemoveConditionForPdpFg;

  // In case of pdp-fg, there will be instances where current offerId is different than previous offerId due to offer setup.
  // So we can't rely on calculating isEligible variable based on offer ids as it will go on to auto remove the gift product.
  if (isPdpFg) {
    isEligible = true;
  } else {
    isEligible = existingOfferID === mr_offer_id;
  }

  if (!skipExecuteFgConditions) {
    const { isCartChanged } = await executeFreegiftConditions({
      mrCart,
      availingType: freeGiftAvailingType,
      widget,
      reservedItemQuantity,
      isEligible,
      canBeAutoRemoved,
    });

    if (isCartChanged) {
      // cant use mrCart any more
      // eslint-disable-next-line no-param-reassign
      mrCart = null;
    }
  }

  const configs = getManualFreeGiftsConfig();
  const config = {
    mr_offer_id,
    offered,
    primary: { sp_variant_id: primaryVariantId },
    isCartRangeTriggerEnabled,
    cartRange,
    cartDiscountText: theme.cartDiscountText,
    isAutoRemove: isCartQuantityRestricted,
    isAutoAdd: false,
    ttl: new Date().getTime() + freeGiftlocalStorageTTL,
    currencyCode,
    defaultCurrencyCartRange,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isDiscountQuantityRestricted,
    isNumberOfOfferedProductsRestricted,
    offerLevelDiscountQuantity,
    discountQuantity,
    isEligibleOnlyForSubscription,
    isRefresh: theme.isRefreshPageAfterProductAddition,
    reservedItemQuantity,
    rulesEligibilityCount,
  };

  configs[widgetID] = config;
  setManualFreeGiftsConfig(JSON.stringify(configs));
  await checkFreeGiftConditionsAndModifyCart({
    offer: config,
    mrCart,
    isEligible: true,
    canBeAutoRemoved,
  });
}

export async function onAutoFreeGiftEligibleResponse({
  offer,
  mrCart,
  widget,
}) {
  const {
    freeGiftAvailingType,
    offered,
    primary: { sp_variant_id: primaryVariantId },
    isCartRangeTriggerEnabled,
    cartRange,
    theme,
    type: offerType,
    isCartQuantityRestricted,
    currencyCode,
    defaultCurrencyCartRange,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isNumberOfOfferedProductsRestricted,
    offerLevelDiscountQuantity,
    discountQuantity,
    isDiscountQuantityRestricted,
    isEligibleOnlyForSubscription,
    freeGiftAutoAddLimit,
    mr_offer_id,
    reservedItemQuantity,
    rulesEligibilityCount,
  } = offer;

  const { id } = widget;
  const widgetID = id.toString();

  if (offerType !== 'freegift') {
    return false;
  }

  if (freeGiftAvailingType !== 'auto_add') {
    return false;
  }

  // in case a new offer is being displayed for the same widget
  // then run the conditions from the previous offer as false
  const widgetOfferMapping = getWidgetOfferMapping();
  const existingOfferID = widgetOfferMapping[widgetID.toString()];
  widgetOfferMapping[widgetID] = mr_offer_id;
  setWidgetOfferMapping(JSON.stringify(widgetOfferMapping));

  // execute the conditions for the previous offer as false
  if (existingOfferID !== mr_offer_id) {
    if (existingOfferID) {
      logRuntimeInfo({
        message: 'Execute free condition for offer ids not matching',
        infoData: {
          existingOfferID,
          mr_offer_id,
          reservedItemQuantity,
        },
        functionName: 'onAutoFreeGiftEligibleResponse',
        fileName: 'freegift',
      });
    }

    const { isCartChanged } = await executeFreegiftConditions({
      mrCart,
      availingType: freeGiftAvailingType,
      widget,
      isEligible: false,
      reservedItemQuantity,
    });
    if (isCartChanged) {
      // cant use mrCart any more
      // eslint-disable-next-line no-param-reassign
      mrCart = null;
    }
  }

  const configs = getAutoAddFreeGiftsConfig();
  const config = {
    mr_offer_id,
    offered,
    primary: { sp_variant_id: primaryVariantId },
    isCartRangeTriggerEnabled,
    cartRange,
    cartDiscountText: theme.cartDiscountText,
    isAutoRemove: isCartQuantityRestricted,
    isAutoAdd: true,
    ttl: new Date().getTime() + freeGiftlocalStorageTTL,
    defaultCurrencyCartRange,
    currencyCode,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isNumberOfOfferedProductsRestricted,
    offerLevelDiscountQuantity,
    discountQuantity,
    isDiscountQuantityRestricted,
    isEligibleOnlyForSubscription,
    freeGiftAutoAddLimit,
    isRefresh: theme.isRefreshPageAfterProductAddition,
    isAutoAddDisabled: widget.triggerType === 'checkout_page',
    reservedItemQuantity,
    rulesEligibilityCount,
    widgetID: Number(widgetID),
  };
  configs[widgetID] = config;

  setAutoFreeGiftsConfig(JSON.stringify(configs));

  const changed = await checkFreeGiftConditionsAndModifyCart({
    offer: config,
    mrCart,
    isEligible: true,
  });
  return changed;
}

export const executeFreegiftConditions = async ({
  mrCart,
  availingType,
  widget,
  isEligible,
  reservedItemQuantity,
  canBeAutoRemoved,
  useExistingConfig,
  existingConfig,
}) => {
  let mutexKey = '';
  let configs = {};

  if (useExistingConfig) configs = existingConfig;
  else if (availingType === 'manual_selection')
    configs = getManualFreeGiftsConfig();
  else configs = getAutoAddFreeGiftsConfig();

  switch (availingType) {
    case 'manual_selection':
      mutexKey = StaticMutexKeys.MANUAL_FREEGIFT;
      break;
    case 'auto_add':
      mutexKey = StaticMutexKeys.AUTO_FREEGIFT;
      break;
    default:
  }

  const mutex = mutexManager.getMutex(mutexKey);
  await mutex.acquire();
  let isCartChanged = false;

  try {
    // if widget is passed then execute for only that widget else execute for all widgets
    if (widget) {
      const { id } = widget;
      const widgetID = id.toString();
      const config = configs[widgetID];
      if (!config) {
        return { isCartChanged };
      }
      if (reservedItemQuantity) {
        config.reservedItemQuantity = reservedItemQuantity;
      }
      config.widgetID = Number(widgetID);
      const changed = await checkFreeGiftConditionsAndModifyCart({
        offer: config,
        mrCart,
        isEligible,
        canBeAutoRemoved,
      });
      return { isCartChanged: changed };
    }

    // as the below code is run for all the configs, there is a chance that the funnel is disabled
    // so delete the config. We are deleting one by one and before the execution as the execution may lead
    // to a refresh and the user will be stuck in an infinite loop

    Object.keys(configs).forEach(async key => {
      const config = configs[key];

      // delete it the config
      delete configs[key];

      if (availingType === 'manual_selection') {
        setManualFreeGiftsConfig(JSON.stringify(configs));
      }

      if (availingType === 'auto_add') {
        setAutoFreeGiftsConfig(JSON.stringify(configs));
      }

      const changed = await checkFreeGiftConditionsAndModifyCart({
        offer: config,
        mrCart,
        isEligible,
        canBeAutoRemoved,
      });

      if (changed) {
        // cant use mrCart any more
        // eslint-disable-next-line no-param-reassign
        mrCart = null;
        isCartChanged = true;
      }
    });
  } finally {
    mutex.release();
  }

  return { isCartChanged };
};

export const fgAddEvent = async ({
  sp_product_id,
  sp_variant_id,
  quantity,
  mr_offer_id,
}) => {
  if (mr_offer_id === '') {
    return;
  }

  try {
    const req = {
      mr_offer_id,
      sp_variant_id,
      quantity,
      sp_product_id,
    };

    const apiURL = ADD_TO_CART_EVENTAPI;
    const httpArgs = [apiURL, req];
    await axios.post(...httpArgs);
    return;
  } catch (e) {
    logger.log(e);
    logRuntimeError({
      errorMessage: 'fg add event failed',
      errorData: {
        fileName: 'freegift',
        level: 'error',
        functionName: 'fgAddEvent',
        extraErrorData: {
          sp_product_id,
          sp_variant_id,
          quantity,
          mr_offer_id,
        },
      },
      errorStack: e,
    });
  }
};

// the below function is used for retrieving the stored pdp fg data and making an offer request from cart drawer/page for auto remove purposes.
export const requestForPdpFgUsingStoredData = async checkEmbeddedWidgetOffersAndDisplayArgs => {
  const storedPdpFgVariantsWithQtyList = getPdpFgVariantsWithQuantity();
  if (storedPdpFgVariantsWithQtyList) {
    const cart = await fetchCart();
    if (cart) {
      const { items: cartItems } = cart;
      const savedPdpFgWidgetList = getPdpFgWidgetList();
      const embeddedModule = await import('./features/embeddedModule');

      storedPdpFgVariantsWithQtyList.forEach(elem => {
        const { variantsWithQuantity } = elem;
        // we need to check if an item from the variantsWithQtyList is present in the cart.
        const isVariansWithQtyPresentInCart = cartItems.some(
          item => item.id === variantsWithQuantity[0].sp_variant_id,
        );
        const widget = savedPdpFgWidgetList.find(
          item => elem.widgetID === item.id,
        );

        let variantsWithQtyToSend = [];
        if (isVariansWithQtyPresentInCart) {
          variantsWithQtyToSend = variantsWithQuantity;
        }

        // if the product from variantsWithQuatity is not present in the cart, we have to send the first cart item as variantsWithQty
        if (!isVariansWithQtyPresentInCart && cartItems.length > 0) {
          const formattedCartItem = {
            sp_product_id: cartItems[0].product_id,
            sp_variant_id: cartItems[0].id,
            quantity: cartItems[0].quantity,
          };
          variantsWithQtyToSend.push(formattedCartItem);
        }

        // below function is used to check embedded widget offers and display widgets.
        embeddedModule.checkEmbeddedWidgetOffersAndDisplay({
          widget,
          customVariantsWithQuantity: variantsWithQtyToSend,
          ...checkEmbeddedWidgetOffersAndDisplayArgs,
        });
      });
    }
  }
};
